'use client';
/**
 * Snackbar component that handles displaying and hiding snackbar messages
 * https://elsevier.atlassian.net/browse/OSMS-302.
 */
import React, { CSSProperties, useEffect, ReactNode } from 'react';
import {
  AnimatePresence,
  MotionStyle,
  TargetAndTransition,
} from 'motion/react';
import * as motion from 'motion/react-m';

import { textGrayInvert } from '@osmosis/tailwind-ui/styles/text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { Button, ButtonProps } from '@osmosis/tailwind-ui/components/Button';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { SnackbarMessage } from './SnackbarMessage';

export type SnackbarProps = {
  isVisible: boolean;
  hide: () => void;
  message?: ReactNode;
  cn?: string;
  autoHide?: number | false;
  onClose?: () => void;
  variant?:
    | 'negative'
    | 'positive'
    | 'primary'
    | 'warning'
    | 'transparent'
    | string;
  motionStyle?: MotionStyle;
  animateProps?: TargetAndTransition;
  action?: { name: ReactNode; handler: () => void; buttonProps?: ButtonProps };
  /** Live-region role so assistive technology announces the notification. */
  role?: React.AriaRole;
};

export type CustomSnackbarProps = Omit<
  SnackbarProps,
  'hide' | 'isVisible' | 'onClose'
>;

/**
 * Snackbar.
 */
export const Snackbar = (props: SnackbarProps) => {
  const {
    cn,
    autoHide = 5000,
    hide,
    message,
    isVisible,
    onClose,
    variant = 'primary',
    motionStyle = {},
    animateProps = {},
    action,
    role = 'status',
  } = props;

  useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(() => {
        hide();
      }, autoHide);

      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const rest = action
    ? {
        ...action.buttonProps,
        ...{ variant: action.buttonProps?.variant || 'transparent' },
      }
    : {};

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          style={
            {
              position: 'fixed',
              maxWidth: '640px',
              left: 20,
              bottom: 20,
              zIndex: ZIndex.HEADER,
              ...motionStyle,
            } as CSSProperties
          }
          initial={{ opacity: 0, y: 40, scale: 0.75 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            ...animateProps,
          }}
          exit={{ opacity: 0, y: 20, transition: { ease: 'easeInOut' } }}
          key="snackbar"
        >
          <SnackbarMessage
            variant={variant}
            message={message}
            cn={cn}
            role={role}
            button={
              action && (
                <Button
                  onClick={() => {
                    action?.handler();
                    hide();
                  }}
                  data-testid="snackbar-action-button"
                  {...rest}
                >
                  {action?.name}
                </Button>
              )
            }
            icon={
              <IconButton
                className={`${textGrayInvert} grow-0 text-lg`}
                data-testid="snackbar-close-button"
                icon={<Icon icon={faXmark} />}
                variant="transparent"
                onClick={() => {
                  onClose?.();
                  hide();
                }}
              />
            }
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
